import { useEffect } from "react";
import { useLang, localePath } from "@/i18n";

const SITE_URL = "https://ibpraxis.net";
const DEFAULT_OG = `${SITE_URL}/og.png`;

type JsonLd = Record<string, unknown> | Record<string, unknown>[];

interface ArticleMeta {
  publishedTime?: string | null;
  modifiedTime?: string | null;
  author?: string;
  section?: string;
  tags?: string[];
}

interface SeoOptions {
  title: string;
  description?: string;
  keywords?: string;
  canonical?: string; // canonical (German) app path, e.g. "/blog"
  /** Override the German path of this page (for pages with language-specific slugs) */
  pathDe?: string;
  /** Override the English path (without /en prefix) of this page */
  pathEn?: string;
  /** Set false when no English version of this page exists */
  hasEnglish?: boolean;
  ogImage?: string;
  ogImageAlt?: string;
  ogImageWidth?: number;
  ogImageHeight?: number;
  ogType?: "website" | "article";
  ogLocale?: string;
  article?: ArticleMeta;
  jsonLd?: JsonLd | null;
  /** id for the JSON-LD <script> tag so we can update/remove it cleanly */
  jsonLdId?: string;
  /** Set true for pages that must not be indexed (e.g. 404, admin) */
  noindex?: boolean;
}


const setMeta = (selector: string, attr: "content", value: string, create?: () => HTMLElement) => {
  let el = document.head.querySelector(selector) as HTMLElement | null;
  if (!el && create) {
    el = create();
    document.head.appendChild(el);
  }
  if (el) el.setAttribute(attr, value);
};

const setCanonical = (href: string) => {
  let el = document.head.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement("link");
    el.rel = "canonical";
    document.head.appendChild(el);
  }
  el.href = href;
};

export const absoluteUrl = (path: string) =>
  path.startsWith("http") ? path : `${SITE_URL}${path.startsWith("/") ? "" : "/"}${path}`;

const ARTICLE_META_KEYS = [
  'meta[property="article:published_time"]',
  'meta[property="article:modified_time"]',
  'meta[property="article:author"]',
  'meta[property="article:section"]',
];

const removeAll = (selector: string) => {
  document.head.querySelectorAll(selector).forEach((el) => el.remove());
};

export const useSeo = (opts: SeoOptions) => {
  const {
    title,
    description,
    keywords,
    canonical,
    pathDe,
    pathEn,
    hasEnglish = true,
    ogImage = DEFAULT_OG,
    ogImageAlt,
    ogImageWidth,
    ogImageHeight,
    ogType = "website",
    ogLocale,
    article,
    jsonLd,
    jsonLdId = "page-jsonld",
    noindex = false,
  } = opts;


  const lang = useLang();

  useEffect(() => {
    document.title = title;

    setMeta('meta[name="robots"]', "content", noindex ? "noindex, follow" : "index, follow", () => {
      const m = document.createElement("meta");
      m.setAttribute("name", "robots");
      return m;
    });



    if (description) {
      setMeta('meta[name="description"]', "content", description, () => {
        const m = document.createElement("meta");
        m.setAttribute("name", "description");
        return m;
      });
      setMeta('meta[property="og:description"]', "content", description, () => {
        const m = document.createElement("meta");
        m.setAttribute("property", "og:description");
        return m;
      });
      setMeta('meta[name="twitter:description"]', "content", description, () => {
        const m = document.createElement("meta");
        m.setAttribute("name", "twitter:description");
        return m;
      });
    }

    if (keywords) {
      setMeta('meta[name="keywords"]', "content", keywords, () => {
        const m = document.createElement("meta");
        m.setAttribute("name", "keywords");
        return m;
      });
    }

    setMeta('meta[property="og:title"]', "content", title, () => {
      const m = document.createElement("meta");
      m.setAttribute("property", "og:title");
      return m;
    });
    setMeta('meta[name="twitter:title"]', "content", title, () => {
      const m = document.createElement("meta");
      m.setAttribute("name", "twitter:title");
      return m;
    });

    setMeta('meta[property="og:type"]', "content", ogType, () => {
      const m = document.createElement("meta");
      m.setAttribute("property", "og:type");
      return m;
    });

    setMeta('meta[property="og:locale"]', "content", ogLocale ?? (lang === "en" ? "en_GB" : "de_AT"), () => {
      const m = document.createElement("meta");
      m.setAttribute("property", "og:locale");
      return m;
    });

    if (ogImage) {
      setMeta('meta[property="og:image"]', "content", ogImage, () => {
        const m = document.createElement("meta");
        m.setAttribute("property", "og:image");
        return m;
      });
      setMeta('meta[name="twitter:image"]', "content", ogImage, () => {
        const m = document.createElement("meta");
        m.setAttribute("name", "twitter:image");
        return m;
      });
      if (ogImageAlt) {
        setMeta('meta[property="og:image:alt"]', "content", ogImageAlt, () => {
          const m = document.createElement("meta");
          m.setAttribute("property", "og:image:alt");
          return m;
        });
        setMeta('meta[name="twitter:image:alt"]', "content", ogImageAlt, () => {
          const m = document.createElement("meta");
          m.setAttribute("name", "twitter:image:alt");
          return m;
        });
      }
      if (ogImageWidth) {
        setMeta('meta[property="og:image:width"]', "content", String(ogImageWidth), () => {
          const m = document.createElement("meta");
          m.setAttribute("property", "og:image:width");
          return m;
        });
      }
      if (ogImageHeight) {
        setMeta('meta[property="og:image:height"]', "content", String(ogImageHeight), () => {
          const m = document.createElement("meta");
          m.setAttribute("property", "og:image:height");
          return m;
        });
      }
    }

    const basePath = canonical ?? window.location.pathname;
    const dePath = pathDe ?? basePath;
    const enPath = pathEn ?? basePath;
    const canonicalUrl = absoluteUrl(
      lang === "en" ? localePath("en", enPath) : dePath,
    );
    setCanonical(canonicalUrl);

    // hreflang alternates
    removeAll('link[rel="alternate"][hreflang]');
    const addAlternate = (hreflang: string, href: string) => {
      const l = document.createElement("link");
      l.rel = "alternate";
      l.setAttribute("hreflang", hreflang);
      l.href = href;
      document.head.appendChild(l);
    };
    addAlternate("de", absoluteUrl(dePath));
    addAlternate("x-default", absoluteUrl(dePath));
    if (hasEnglish) addAlternate("en", absoluteUrl(localePath("en", enPath)));
    setMeta('meta[property="og:url"]', "content", canonicalUrl, () => {
      const m = document.createElement("meta");
      m.setAttribute("property", "og:url");
      return m;
    });

    // Article-specific OG tags — clear previous, then add fresh
    ARTICLE_META_KEYS.forEach(removeAll);
    removeAll('meta[property="article:tag"]');
    if (ogType === "article" && article) {
      const addProp = (prop: string, value?: string | null) => {
        if (!value) return;
        const m = document.createElement("meta");
        m.setAttribute("property", prop);
        m.setAttribute("content", value);
        document.head.appendChild(m);
      };
      addProp("article:published_time", article.publishedTime ?? undefined);
      addProp("article:modified_time", article.modifiedTime ?? undefined);
      addProp("article:author", article.author);
      addProp("article:section", article.section);
      (article.tags ?? []).forEach((t) => addProp("article:tag", t));
    }

    // JSON-LD
    const existing = document.getElementById(jsonLdId);
    if (jsonLd) {
      const script = (existing as HTMLScriptElement | null) ?? document.createElement("script");
      script.id = jsonLdId;
      script.type = "application/ld+json";
      script.text = JSON.stringify(jsonLd);
      if (!existing) document.head.appendChild(script);
    } else if (existing) {
      existing.remove();
    }

    return () => {
      const s = document.getElementById(jsonLdId);
      if (s) s.remove();
      ARTICLE_META_KEYS.forEach(removeAll);
      removeAll('meta[property="article:tag"]');
      removeAll('link[rel="alternate"][hreflang]');
    };
  }, [lang, noindex, title, description, keywords, canonical, pathDe, pathEn, hasEnglish, ogImage, ogImageAlt, ogImageWidth, ogImageHeight, ogType, ogLocale, article, jsonLd, jsonLdId]);
};
